import React, { useCallback, useEffect, useMemo } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
  MarkUnreadChatAltOutlined as MarkUnreadChatAltOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  SensorsRounded as SensorsRoundedIcon,
} from "@mui/icons-material";
import GlobalDashboardSidebar from "../admin/GlobalDashboardSidebar";
import useGlobalSidebarItems from "../admin/useGlobalSidebar";
import LicensePortalModal from "../modals/LicensePortalModal";
import { USER_ROLE_ROUTE_CONFIG } from "../users/userRoleConfig";
import { getDemoReadOnlyMessage, isDemoReadOnlyUser } from "../../utils/demoUser";
import { hasPermission, normalizeRole, ROLES } from "../../utils/rbacHelper";
import { logout } from "../../store/authSlice";
import "../admin/dashboard-redesign.css";

const BILLING_MENU_ITEMS = [
  { key: "licenses", label: "Licenses" },
  { key: "all-licenses", label: "All Licenses" },
  { key: "purchase-license", label: "Purchase License" },
  { key: "license-summary", label: "License Summary" },
  { key: "transactions", label: "Transactions" },
];

const TASK_MENU_ITEMS = [
  { key: "all", label: "All Tasks" },
  { key: "create", label: "Create Task" },
  { key: "inventory", label: "Inventory" },
];

const MESSAGE_MENU_ITEMS = [
  { key: "compose", label: "Compose & Send" },
  { key: "templates", label: "Templates" },
  { key: "delivery-reports", label: "Delivery Reports" },
  { key: "scheduled", label: "Scheduled Messages" },
];

const EXPENSE_MENU_ITEMS = [
  { key: "create", label: "Create Expense", path: "/expense/create" },
  { key: "list", label: "Expense List", path: "/expense/list" },
  { key: "upload", label: "Upload Expense", path: "/expense/upload" },
];

const SENSOR_MENU_ITEMS = [
  { key: "list", label: "Sensor List", path: "/sensors/list" },
  { key: "create", label: "Add Sensor", path: "/sensors/create" },
  { key: "upload", label: "Upload Sensors", path: "/sensors/upload" },
];

export default function LicensesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.auth.user);
  const isDemoReadOnly = isDemoReadOnlyUser(currentUser);
  const demoReadOnlyMessage = getDemoReadOnlyMessage(currentUser);
  const normalizedRole = normalizeRole(currentUser?.role);
  const canPurchaseLicense =
    hasPermission(normalizedRole, "purchaseLicense") && normalizedRole !== ROLES.DEALER;
  const visibleBillingMenuItems = useMemo(
    () =>
      BILLING_MENU_ITEMS.filter((item) => {
        if (item.key !== "purchase-license") return true;
        return !isDemoReadOnly && canPurchaseLicense;
      }),
    [canPurchaseLicense, isDemoReadOnly]
  );

  const selectedSection = searchParams.get("section") || "licenses";
  const selectedSectionLabel =
    visibleBillingMenuItems.find((item) => item.key === selectedSection)?.label || "Licenses";
  const profileName = currentUser?.name || currentUser?.username || "User";
  const roleLabel = String(currentUser?.role || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const setSelectedSection = useCallback(
    (section) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set("section", section);
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (selectedSection !== "purchase-license") return;
    if (!isDemoReadOnly && canPurchaseLicense) return;
    setSelectedSection("licenses");
  }, [canPurchaseLicense, isDemoReadOnly, selectedSection, setSelectedSection]);

  const globalSidebarItemsLegacy = useMemo(
    () => [
      {
        key: "dashboard",
        label: "Dashboard",
        icon: <AssessmentOutlinedIcon fontSize="small" />,
        active: location.pathname === "/dashboard",
        onClick: () => navigate("/dashboard"),
      },
      {
        key: "vehicles",
        label: "Vehicles",
        icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />,
        active: location.pathname === "/admin-dashboard" && location.search.includes("tab=devices"),
        onClick: () => navigate("/admin-dashboard?tab=devices"),
      },
      {
        key: "live-tracking",
        label: "Live Tracking",
        icon: <PlaceOutlinedIcon fontSize="small" />,
        active: location.pathname === "/admin-dashboard" && location.search.includes("tab=map"),
        onClick: () => navigate("/admin-dashboard?tab=map"),
      },
      {
        key: "trips",
        label: "Trips",
        icon: <RouteOutlinedIcon fontSize="small" />,
        active: location.pathname === "/details",
        onClick: () => navigate("/logistic/trips"),
      },
      {
        key: "geofences",
        label: "Geofences",
        icon: <PlaceOutlinedIcon fontSize="small" />,
        active: location.pathname === "/geofences",
        onClick: () => navigate("/geofences"),
      },
      {
        key: "notifications",
        label: "Notifications",
        icon: <NotificationsActiveOutlinedIcon fontSize="small" />,
        active: location.pathname === "/notifications",
        onClick: () => navigate("/notifications"),
      },
      {
        key: "reports",
        label: "Reports",
        icon: <ReceiptLongOutlinedIcon fontSize="small" />,
        active: location.pathname === "/reports",
        onClick: () => navigate("/reports"),
      },
      {
        key: "tasks",
        label: "Tasks",
        icon: <TaskAltOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/tasks"),
        children: TASK_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === `/tasks/${item.key}`,
          onClick: () => navigate(`/tasks/${item.key}`),
        })),
      },
      {
        key: "messages",
        label: "Messages",
        icon: <MarkUnreadChatAltOutlinedIcon fontSize="small" />,
        active: location.pathname === "/message",
        children: MESSAGE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === "/message" && (new URLSearchParams(location.search).get("section") || "compose") === item.key,
          onClick: () => navigate(`/message?section=${item.key}`),
        })),
      },
      {
        key: "logistic",
        label: "Logistic",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/logistic"),
        children: [
          { key: "logistics-trips", label: "Trips", active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
          { key: "logistics-clients", label: "Clients", active: location.pathname === "/logistic/clients", onClick: () => navigate("/logistic/clients") },
          { key: "logistics-materials", label: "Materials", active: location.pathname === "/logistic/materials", onClick: () => navigate("/logistic/materials") },
          { key: "logistics-documents", label: "Documents", active: location.pathname === "/logistic/documents", onClick: () => navigate("/logistic/documents") },
        ],
      },
      {
        key: "organization",
        label: "Groups",
        icon: <Groups2OutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/groups"),
        onClick: () => navigate("/groups"),
      },
      {
        key: "reminders",
        label: "Reminders",
        icon: <EventNoteOutlinedIcon fontSize="small" />,
        active: location.pathname === "/reminders",
        onClick: () => navigate("/reminders"),
      },
      {
        key: "routes",
        label: "Routes",
        icon: <RouteOutlinedIcon fontSize="small" />,
        active: location.pathname === "/routes",
        onClick: () => navigate("/routes"),
      },
      {
        key: "expense",
        label: "Expense",
        icon: <PaidOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/expense"),
        children: EXPENSE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "sensors",
        label: "Sensors",
        icon: <SensorsRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/sensors"),
        children: SENSOR_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "users",
        label: "Users & Roles",
        icon: <PersonOutlineRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/users"),
        children: USER_ROLE_ROUTE_CONFIG.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "billing",
        label: "Billing & Licenses",
        icon: <AssessmentOutlinedIcon fontSize="small" />,
        active: location.pathname === "/licenses",
        defaultExpanded: true,
        children: visibleBillingMenuItems.map((item) => ({
          key: item.key,
          label: item.label,
          active: selectedSection === item.key,
          onClick: () => setSelectedSection(item.key),
        })),
      },
      {
        key: "settings",
        label: "Settings",
        icon: <SettingsOutlinedIcon fontSize="small" />,
        active: location.pathname === "/profile",
        onClick: () => navigate("/profile"),
      },
    ],
    [location.pathname, location.search, navigate, selectedSection, setSelectedSection, visibleBillingMenuItems]
  );
  const globalSidebarItems = useGlobalSidebarItems();
  void globalSidebarItemsLegacy;

  return (
    <div className="dashboard-page-shell">
      <GlobalDashboardSidebar
        items={globalSidebarItems}
        profileName={profileName}
        roleLabel={roleLabel}
        onSupport={isDemoReadOnly ? undefined : () => navigate("/message")}
        onLogout={() => dispatch(logout())}
      />

      <main className="dashboard-content" style={{ gap: 18 }}>
        <Box>
          <Typography sx={{ mt: 0.2, color: "#7c8ba1", fontSize: 13.5 }}>
            Home / Billing & Licenses / {selectedSectionLabel}
          </Typography>
          <Typography sx={{ mt: 1.1, fontSize: { xs: "2rem", md: "2.35rem" }, fontWeight: 900, color: "#0f172a" }}>
            Licenses
          </Typography>
          <Typography sx={{ mt: 0.6, color: "#64748b", fontSize: 15.5 }}>
            Manage and view all purchased licenses in your account
          </Typography>
          {isDemoReadOnly ? (
            <Alert
              severity="warning"
              sx={{
                mt: 2,
                borderRadius: 3,
                border: "1px solid rgba(245, 158, 11, 0.22)",
                background: "linear-gradient(135deg, rgba(255, 247, 237, 0.98) 0%, rgba(255,255,255,0.98) 100%)",
                boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
                alignItems: "flex-start",
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#b45309" }}>
                Guided Demo Mode
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: "1rem", fontWeight: 700, color: "#111827" }}>
                Billing stays protected here, but the earning opportunity is real.
              </Typography>
              <Typography sx={{ mt: 0.75, fontSize: "0.95rem", lineHeight: 1.7, color: "#4b5563" }}>
                {demoReadOnlyMessage}
              </Typography>
            </Alert>
          ) : null}
        </Box>

        <LicensePortalModal asPage initialSection={selectedSection} />
      </main>
    </div>
  );
}
